import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import SuperUserLoanPageSF from '../../../templates/SuperUserLoanPageSF.js'
import CardUI from '../../../widgets/layout/CardUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import GenericErrorUI from '../../../components/common/GenericErrorUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import './OnBoardingDataPageUI.css'

const isValuePresent = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'nan') return false
    return true
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) return value.some((item) => isValuePresent(item))
    return Object.keys(value).some((key) => isValuePresent(value[key]))
  }
  return true
}

const objectHasAnyData = (obj, keysToIgnore = ['accountName']) => {
  if (!obj || typeof obj !== 'object') return false
  return Object.entries(obj).some(([key, val]) => {
    if (keysToIgnore.includes(key)) return false
    return isValuePresent(val)
  })
}

const formatAttributeValue = (value, isPercentage = false) => {
  if (!isValuePresent(value)) return 'NA'
  if (isPercentage) {
    if (typeof value === 'number') return value + '%'
    const trimmed = String(value).trim()
    return trimmed.endsWith('%') ? trimmed : trimmed + '%'
  }
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN')
  }
  return String(value).trim()
}

const formatCurrencyInr0 = (amount) => {
  if (!isValuePresent(amount)) return null
  if (typeof amount === 'string') {
    const trimmed = amount.trim()
    if (trimmed.startsWith('₹') || /[,₹]/.test(trimmed)) return trimmed
    const n = Number(trimmed.replace(/,/g, ''))
    if (!Number.isFinite(n)) return amount
    amount = n
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  } catch (e) {
    return amount
  }
}

const formatCurrencyAttributeValue = (value) => {
  if (!isValuePresent(value)) return 'NA'
  const formatted = formatCurrencyInr0(value)
  return formatted != null ? formatted : 'NA'
}

const hasRemarkValue = (remark) => {
  if (!isValuePresent(remark)) return false
  const str = String(remark).trim().toLowerCase()
  return str !== 'na' && str !== 'n/a'
}

const Row = ({ label, value, remark }) => (
  <div className='g_m_bottom_10'>
    <div className='g_flex_h g_flex_between'>
      <span className='g_label'>{label}:</span>
      <span className='g_value'>{value}</span>
    </div>
    {hasRemarkValue(remark) && (
      <div className='g_muted g_small g_m_top_4' style={{ fontStyle: 'italic' }}>
        Remarks: {String(remark).trim()}
      </div>
    )}
  </div>
)

const EmptyState = ({ text = 'No data available.' }) => (
  <div className='g_flex_v g_flex_center g_m_top_10 g_m_bottom_10'>
    <span className='icon icon-file empty-icon'></span>
    <div className='g_m_top_10 g_text'>{text}</div>
  </div>
)

const Section = ({ title, children }) => (
  <div className='g_m_top_20'>
    <CardUI title={title}>
      {children}
    </CardUI>
  </div>
)

const formatMonthYear = (key) => {
  if (!key || typeof key !== 'string') return String(key)
  const trimmed = key.trim()
  const parts = trimmed.split('-')
  if (parts.length === 2) {
    const year = parts[0]
    const monthNum = parseInt(parts[1], 10)
    if (monthNum >= 1 && monthNum <= 12) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months[monthNum - 1] + ' ' + year
    }
  }
  return trimmed
}

const ExpandableMapRow = ({ label, mapObj, formatValue = (v) => (typeof v === 'number' ? v.toLocaleString('en-IN') : String(v)) }) => {
  const [expanded, setExpanded] = useState(false)

  if (!mapObj || typeof mapObj !== 'object' || Array.isArray(mapObj) || !isValuePresent(mapObj)) {
    return <Row label={label} value='NA' />
  }

  const validEntries = Object.entries(mapObj).filter(([, val]) => isValuePresent(val))
  if (validEntries.length === 0) {
    return <Row label={label} value='NA' />
  }

  return (
    <div className='g_m_bottom_12 map-expandable-section'>
      <div
        className='g_flex_h g_flex_between g_cursor_pointer map-header-row'
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', padding: '4px 0' }}
      >
        <span className='g_label g_flex_h g_flex_left'>
          <span className={'icon ' + (expanded ? 'icon-angle-down' : 'icon-angle-right') + ' g_m_right_8'} style={{ marginTop: '2px' }} />
          <span style={{ fontWeight: 'bold' }}>{label}</span>
          <span className='g_muted g_small' style={{ fontWeight: 'normal', marginLeft: '16px' }}>
            ({validEntries.length})
          </span>
        </span>
      </div>

      {expanded && (
        <div className='map-details-container g_m_top_8 g_m_bottom_12' style={{ padding: '8px 12px', backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d0d0d0' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '11px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase' }}>
                  MONTH YEAR
                </th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '11px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase' }}>
                  AMOUNT
                </th>
              </tr>
            </thead>
            <tbody>
              {validEntries.map(([yearMonthKey, val], idx) => (
                <tr key={yearMonthKey} style={{ borderBottom: idx < validEntries.length - 1 ? '1px solid #eeeeee' : 'none' }}>
                  <td style={{ textAlign: 'left', padding: '6px 8px', fontSize: '13px', color: '#333333' }}>
                    {formatMonthYear(yearMonthKey)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '13px', color: '#111111', fontWeight: '500' }}>
                    {formatValue(val)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const OnBoardingDataPageUI = ({ context, errorList, loan, loading, onboardingData, onDownloadProvFileClick, onDownloadProjectFileClick, onDownloadCustomerFileClick, onRejectLoanClick, onRefreshClick }) => {
  const hasData = !!(loan && onboardingData)

  const prov = (onboardingData && onboardingData.provisionalFinancials) || null
  const proj = (onboardingData && onboardingData.projectedCashflows) || null

  const hasProvData = objectHasAnyData(prov)
  const hasProjData = objectHasAnyData(proj)

  return (
    <SuperUserLoanPageSF
      context={context}
      className='pg_su_onboarding_data'
      backLink={'/su/loans' + (loan && (loan.status === 'closed' ? '/closed' : (loan.status === 'disbursed' ? '/disbursed' : '')))}
      headerText={loan ? (loan.accountName + ' ' + (loan.loanName || '')) : 'Loan'}
      centered={false}
      loan={loan}
      page='onboardingData'
      dimension='w1600'
    >
      <LRTableActionsUI
        location='top'
        left={
          <span className='g_flex_left g_flex_h'>
            <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />

            <span className='g_m_left_100 g_flex_v'>
              <span className='g_m_left_0 g_large'>
                <b>Onboarding Data{onboardingData && onboardingData.accountName ? ': ' + onboardingData.accountName : ''}</b>
              </span>
              {onboardingData && onboardingData.appOnboardingStatus && isValuePresent(onboardingData.appOnboardingStatus) && (
                <span className='g_m_left_0 g_m_top_5 g_large'>
                  ({onboardingData.appOnboardingStatus})
                </span>
              )}
            </span>
          </span>
        }
        right={
          loan ? (
            <span className='g_flex_h g_flex_right onboarding-actions-container'>
              <Link
                className='g_btn'
                to={'/su/accounts/' + loan.accountId + '/dataCollection'}
              >
                Go to client data
              </Link>
              {loan && loan.loanId && (
                <Link
                  className='g_btn g_m_left_10'
                  to={'/su/loans/' + loan.loanId + '/broadTerms'}
                >
                  Generate Broad Terms
                </Link>
              )}
              {onboardingData && onboardingData.appOnboardingStatus === 'waiting_for_approval' && (
                <>
                  <button
                    className='g_btn reject-loan-btn'
                    type='button'
                    onClick={onRejectLoanClick}
                  >
                    Reject Loan
                  </button>
                </>
              )}
            </span>
          ) : (<span />)
        }
      />

      {loading && (
        <div className='g_flex_v g_flex_center g_m_top_100' style={{ minHeight: '350px', width: '100%' }}>
          <SpinnerComponentUI />
        </div>
      )}
      {errorList && errorList.length > 0 && <GenericErrorUI error={errorList[0]} />}

      {!loading && hasData && (
        <div className='g_flex_h g_flex_top g_m_top_50 main-content-container'>
          <div className='content-column'>
            <Section title='Provisional Financials'>
              <div>
                {hasProvData ? (
                  <>
                    <Row label='Financial Year' value={formatAttributeValue(prov.financialYear)} />
                    <Row label='Total Revenue' value={formatCurrencyAttributeValue(prov.totalRevenue)} />
                    <Row label='Finance Cost' value={formatCurrencyAttributeValue(prov.financeCost)} />
                    <Row label='Fixed Assets' value={formatCurrencyAttributeValue(prov.fixedAssets)} />
                    <Row label='Current Cash Balance' value={formatCurrencyAttributeValue(prov.currentCashBalance)} />
                    <Row label='Direct Expense' value={formatCurrencyAttributeValue(prov.directExpense)} />
                    <Row label='Overheads' value={formatCurrencyAttributeValue(prov.overheads)} />
                  </>
                ) : (
                  <EmptyState text='No financial information available' />
                )}
              </div>
            </Section>

            <Section title='Provisional Financials Files'>
              <div>
                {(() => {
                  const files = prov ? (prov.provisionalFinancialsFiles || prov.provisionalFinancialsFileList || prov.fileList || []) : []
                  if (!files || files.length === 0) {
                    return <EmptyState text='No files available.' />
                  }
                  return files.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_m_bottom_10'>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => { e.preventDefault(); onDownloadProvFileClick && onDownloadProvFileClick(fileName) }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                      </div>
                    )
                  })
                })()}
              </div>
            </Section>

            {/* Projected Cashflows Information Section */}
            <Section title='Projected Cashflows Information'>
              <div>
                {hasProjData ? (
                  <>
                    <ExpandableMapRow label='Expected Equity Rate' mapObj={proj.expectedEquityRate} />
                    <ExpandableMapRow label='Expected Debt Raise' mapObj={proj.expectedDebtRaise} />
                    <ExpandableMapRow label='Expected Capital Expenditure / Inventory' mapObj={proj.expectedCaptalExpenditureInvertory || proj.expectedCapitalExpenditureInventory} />
                    <Row label='Avg MOM Increase Revenue' value={formatAttributeValue(proj.avgMOMIncreaseRevenuePercentage, true)} remark={proj.avgMOMIncreaseRevenueRemarks} />
                    <Row label='Avg MOM Increase Direct Expense' value={formatAttributeValue(proj.avgMOMIncreaseDirectExpensePercentage, true)} remark={proj.avgMOMIncreaseDirectExpenseRemarks} />
                    <Row label='Avg MOM Increase Overheads' value={formatAttributeValue(proj.avgMOMIncreaseOverheadsPercentage, true)} remark={proj.avgMOMIncreaseOverheadsRemarks} />
                    <Row label='Avg Cost of Borrowing' value={formatAttributeValue(proj.avgCostBorrowingPercentage, true)} remark={proj.avgCostBorrowingRemarks} />
                  </>
                ) : (
                  <EmptyState text='No projected cashflows information available' />
                )}
              </div>
            </Section>

            {/* Projected Cashflows Documents Section */}
            <Section title='Projected Cashflows Documents'>
              <div>
                {(() => {
                  const files = proj ? (proj.fileList || proj.projectedCashflowsFileList || []) : []
                  if (!files || files.length === 0) {
                    return <EmptyState text='No files available.' />
                  }
                  return files.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_m_bottom_10'>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            onDownloadProjectFileClick && onDownloadProjectFileClick(fileName)
                          }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                      </div>
                    )
                  })
                })()}
              </div>
            </Section>

            <Section title='Requested Loan Details'>
              <div>
                {(() => {
                  const l = onboardingData.loanDetails || {}
                  const map = [
                    ['requestedAmount', 'Requested Amount'],
                    ['requestedTenureMonths', 'Requested Tenure (months)'],
                    ['purpose', 'Purpose'],
                    ['projectType', 'Project Type'],
                    ['requestDesc', 'Description'],
                    ['loanType', 'Loan Type'],
                    ['loanSubType', 'Loan Sub Type']
                  ]
                  const rows = []
                  map.forEach(([key, label]) => {
                    const v = l[key]
                    if (isValuePresent(v)) {
                      let value = v
                      if ((typeof v === 'number') && key.toLowerCase().includes('amount')) value = formatCurrencyInr0(v)
                      rows.push(<Row key={key} label={label} value={String(value)} />)
                    }
                  })
                  return rows.length > 0 ? rows : <EmptyState text='No loan request details available.' />
                })()}
              </div>
            </Section>
          </div>

          <div className='content-column'>
            <Section title='Project Details'>
              <div>
                {(() => {
                  const p = onboardingData.projectDetails || {}
                  const fmtDate = (d) => {
                    if (typeof d === 'string') return d
                    try {
                      const n = Number(d)
                      if (!Number.isFinite(n)) return String(d)
                      const ms = n > 1e12 ? n : (n * 1000)
                      return new Date(ms).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
                    } catch (e) {
                      return String(d)
                    }
                  }
                  const fmtCurr = (n) => formatCurrencyInr0(n)

                  const RowIf = ({ label, value, format }) => {
                    if (!isValuePresent(value)) return null
                    const v = format ? format(value) : value
                    return <div className='g_flex_h g_flex_between g_m_bottom_10'><span className='g_label'>{label}:</span><span className='g_value'>{v}</span></div>
                  }

                  if (p.projectType === 'solar_ppa') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='Offtaker PAN' value={p.offtakerCounterpartyPan} />
                          <RowIf label='Solar Project Capacity (KWs)' value={p.solarProjectCapacity} format={v => v + ' KW'} />
                          <RowIf label='Tariff (per KWh)' value={p.tariff} />
                          <RowIf label='Location' value={p.location} />
                          <RowIf label='Tenure of PPA (years)' value={p.tenure} format={v => v + ' years'} />
                          <RowIf label='Lock-in Period (years)' value={p.lockInPeriod} format={v => v + ' years'} />
                          <RowIf label='Expected PLF (%)' value={p.expectedPlf} format={v => v + '%'} />
                          <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                          <RowIf label='Project Commission Date' value={p.commissioningDate} format={fmtDate} />
                        </div>
                      </div>
                    )
                  }

                  if (p.projectType === 'po') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='CounterParty PAN' value={p.offtakerCounterpartyPan} />
                          <RowIf label='Order Value' value={p.orderValue} format={v => v.toLocaleString()} />
                          <RowIf label='Project Cost' value={p.projectCost} format={v => v.toLocaleString()} />
                          <RowIf label='PO Expiry Date' value={p.poExpiryDate} format={fmtDate} />
                          <RowIf label='Expected Margin' value={p.expectedMarginPercent} format={v => v + '%'} />
                        </div>
                      </div>
                    )
                  }

                  if (p.projectType === 'logistics') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                        </div>
                        {Array.isArray(p.logisticsContractList) && p.logisticsContractList.length > 0 ? (
                          <div className='project-section project-list-section'>
                            <h4 className='section-title'>Logistics Contracts</h4>
                            {p.logisticsContractList.map((c, i) => (
                              <div key={i} className='g_m_bottom_16 project-item'>
                                <h5 className='project-heading'>Contract {i + 1}</h5>
                                <RowIf label='Counterparty PAN' value={c.counterpartyPan && c.counterpartyPan.trim()} />
                                <RowIf label='Contract Tenure' value={c.contractTenure} format={v => v + ' months'} />
                                <RowIf label='Number of Vehicles' value={c.numberOfVehicles} />
                                <RowIf label='Vehicle Make' value={c.vehicleMake} />
                                <RowIf label='Vehicle Model' value={c.vehicleModel} />
                                <RowIf label='Vehicle Cost' value={c.vehicleCost} format={fmtCurr} />
                                <RowIf label='Revenue Per Month Per Vehicle' value={c.revenuePerMonthPerVehicle} format={fmtCurr} />
                                <RowIf label='Direct Cost Per Month Per Vehicle' value={c.directCostPerMonthPerVehicle} format={fmtCurr} />
                                <RowIf label='Total Cost' value={c.totalCost} format={fmtCurr} />
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )
                  }

                  if (p.projectType === 'rental_subscriptions') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='Number of Vehicles' value={p.numberOfVehicles} />
                          <RowIf label='Vehicle Make' value={p.vehicleMake} />
                          <RowIf label='Vehicle Model' value={p.vehicleModel} />
                          <RowIf label='Vehicle Cost' value={p.vehicleCost} format={fmtCurr} />
                          <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                          <RowIf label='Revenue Per Month' value={p.revenuePerMonth} format={fmtCurr} />
                          <RowIf label='Direct Cost Per Month' value={p.directCostPerMonth} format={fmtCurr} />
                        </div>
                      </div>
                    )
                  }

                  if (p.projectType === 'asset_management') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                          <RowIf label='Counterparty PAN' value={p.offtakerCounterpartyPan} />
                          <RowIf label='Tenure of Contract' value={p.contractTenure} format={v => v + ' months'} />
                          <RowIf label='Revenue Per Month' value={p.revenuePerMonth} format={fmtCurr} />
                          <RowIf label='Direct Cost Per Month' value={p.directCostPerMonth} format={fmtCurr} />
                        </div>
                        {Array.isArray(p.assetList) && p.assetList.length > 0 ? (
                          <div className='project-section project-list-section'>
                            <h4 className='section-title'>Asset List</h4>
                            {p.assetList.map((a, i) => (
                              <div key={i} className='g_m_bottom_12 project-item'>
                                <h5 className='project-heading'>Asset {i + 1}</h5>
                                <RowIf label='Asset Type' value={a.assetType} />
                                <RowIf label='Number of Assets' value={a.numberOfAssets} />
                                <RowIf label='Asset Make' value={a.assetMake} />
                                <RowIf label='Asset Model' value={a.assetModel} />
                                <RowIf label='Asset Cost' value={a.assetCost} format={fmtCurr} />
                                <RowIf label='Total Cost' value={a.totalCost} format={fmtCurr} />
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )
                  }

                  if (p.projectType === 'cash_flow') {
                    return (
                      <div>
                        <div className='project-section'>
                          <h4 className='section-title'>Basic Information</h4>
                          <RowIf label='Project Name' value={p.projectName} />
                          <RowIf label='Project Type' value={p.projectType} />
                          <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                        </div>
                        {Array.isArray(p.cashFlowList) && p.cashFlowList.length > 0 ? (
                          <div className='project-section project-list-section'>
                            <h4 className='section-title'>Cash Flow Details</h4>
                            {p.cashFlowList.map((cf, i) => (
                              <div key={i} className='g_m_bottom_16 project-item'>
                                <h5 className='project-heading'>Cash Flow {i + 1}</h5>
                                <RowIf label='Material Name' value={cf.materialName} />
                                <RowIf label='Supplier Name' value={cf.supplierName} />
                                <RowIf label='Total Cost' value={cf.totalCost} format={fmtCurr} />
                                <RowIf label='Counterparty PAN' value={cf.counterpartyPan} />
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {Array.isArray(p.paymentModes) && p.paymentModes.length > 0 && (
                          <div className='project-section g_m_top_10'>
                            <RowIf label='B2C Payment Modes' value={p.paymentModes.join(', ')} />
                          </div>
                        )}
                        {Array.isArray(p.customerPanList) && p.customerPanList.length > 0 && (
                          <div className='project-section g_m_top_10'>
                            <RowIf label='B2B Customer PAN(s)' value={p.customerPanList.join(', ')} />
                          </div>
                        )}
                      </div>
                    )
                  }

                  return (
                    <div>
                      <div className='project-section'>
                        <h4 className='section-title'>Basic Information</h4>
                        <RowIf label='Project Name' value={p.projectName} />
                        <RowIf label='Project Type' value={p.projectType} />
                        <RowIf label='Project Cost' value={p.projectCost} format={fmtCurr} />
                      </div>
                      {Array.isArray(p.paymentModes) && p.paymentModes.length > 0 && (
                        <div className='project-section g_m_top_10'>
                          <RowIf label='B2C Payment Modes' value={p.paymentModes.join(', ')} />
                        </div>
                      )}
                      {Array.isArray(p.customerPanList) && p.customerPanList.length > 0 && (
                        <div className='project-section g_m_top_10'>
                          <RowIf label='B2B Customer PAN(s)' value={p.customerPanList.join(', ')} />
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </Section>

            {onboardingData.projectDetails && Array.isArray(onboardingData.projectDetails.projectFileList) && onboardingData.projectDetails.projectFileList.length > 0 && (
              <Section title='Project Files'>
                <div>
                  {onboardingData.projectDetails.projectFileList.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_m_bottom_10'>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => { e.preventDefault(); onDownloadProjectFileClick && onDownloadProjectFileClick(fileName) }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}

            {onboardingData.projectDetails && Array.isArray(onboardingData.projectDetails.customerFileList) && onboardingData.projectDetails.customerFileList.length > 0 && (
              <Section title='B2B Customer invoice(s) / PO(s)'>
                <div>
                  {onboardingData.projectDetails.customerFileList.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_m_bottom_10'>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => { e.preventDefault(); onDownloadCustomerFileClick && onDownloadCustomerFileClick(fileName) }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}
          </div>
        </div>
      )}
    </SuperUserLoanPageSF>
  )
}

export default OnBoardingDataPageUI
